import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

import { useCreatorSales } from '@/hooks/use-creators'
import type { CreatorSale } from '@/lib/api/types'

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente (Garantia)',
  approved: 'Aprovada',
  cancelled: 'Cancelada',
  paid: 'Paga / Payout',
}

interface CreatorSalesTableProps {
  creatorId: string
}

/**
 * Displays a paginated table of sales for a given creator, including date, order ID, customer, amounts, commission, and status badges.
 *
 * @param creatorId - Identifier of the creator whose sales are displayed
 * @returns The rendered sales table UI for the specified creator, or a loading/empty state when appropriate
 */
export function CreatorSalesTable({ creatorId }: CreatorSalesTableProps) {
  const [page, setPage] = useState(1)
  const limit = 10

  const { data: salesResult, isLoading } = useCreatorSales(creatorId, { page, limit })
  const sales =
    (salesResult as { data?: CreatorSale[] })?.data ||
    (salesResult as unknown as CreatorSale[]) ||
    []
  const meta = (salesResult as { meta?: { totalPages: number } })?.meta

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (sales.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground border rounded-lg border-dashed">
        <p>Ainda não há vendas registradas para este creator.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead className="hidden md:table-cell">Pedido</TableHead>
              <TableHead className="hidden sm:table-cell">Cliente</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Comissão</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sales.map((sale: CreatorSale) => (
              <TableRow key={sale.id}>
                <TableCell className="whitespace-nowrap">
                  {format(new Date(sale.createdAt), 'dd/MM/yy', { locale: ptBR })}
                </TableCell>
                <TableCell className="font-mono text-xs hidden md:table-cell">
                  {sale.externalOrderId}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{sale.customerName}</span>
                    <span className="text-xs text-muted-foreground">{sale.customerEmail}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(sale.orderAmount)}
                </TableCell>
                <TableCell className="text-right font-medium whitespace-nowrap">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(sale.commissionAmount)}
                  <div className="text-[10px] text-muted-foreground">
                    ({(sale.commissionRate * 100).toFixed(0)}%)
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className={STATUS_COLORS[sale.status] || ''}>
                    {STATUS_LABELS[sale.status] || sale.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {meta && meta.totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (page > 1) setPage(page - 1)
                }}
              />
            </PaginationItem>

            <PaginationItem>
              <span className="text-sm px-4">
                Página {page} de {meta.totalPages}
              </span>
            </PaginationItem>

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (page < meta.totalPages) setPage(page + 1)
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  )
}
